import React from "react";
import type { ApolloClient } from "@apollo/client";
import IconSpinner from "components/IconSpinner";
import PromisePoller from "components/PromisePoller";
import { refreshUserState } from "actions/userActions";
import Alert from "components/Alert";
import { get, includes } from "lodash";
import { push } from "store/actions/router";
import { setTab } from "actions/navigationActions";
import {
  getProgressiveOnboarding,
  getSetupIntent,
  hydrateDashboard,
} from "actions/employerActions";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { employerConstants } from "actions/types";
import { createErrorSelector } from "store/selectors";
import { hasFullyOnboarded } from "store/selectors/employer";
import {
  EmployerApplicationAdditionalDocumentationSubmitted,
  EmployerApplicationRequiresAdditionalDocumentation,
  EmployerApplicationSubmittedKYB,
  EmployerBillingVerificationPending,
  EmployerPendingKYBSubmission,
  EmployerRegistrationComplete,
  EmployerSelfServeRegistrationComplete,
  EmployerTermsComplete,
} from "statics/states";
import KybDashboard from "pages/dashboards/employerDashboard/dashboard/KybDashboard";
import EmployerDashboard from "pages/dashboards/employerDashboard/dashboard/EmployerDashboard";
import ProgressiveOnboardingDashboard from "pages/dashboards/employerDashboard/dashboard/ProgressiveOnboardingDashboard";
import type { RootState } from "types/state";

const stillKybStates = [
  EmployerApplicationAdditionalDocumentationSubmitted,
  EmployerApplicationRequiresAdditionalDocumentation,
  EmployerApplicationSubmittedKYB,
  EmployerRegistrationComplete,
  EmployerTermsComplete,
  EmployerPendingKYBSubmission,
  EmployerSelfServeRegistrationComplete,
  EmployerBillingVerificationPending,
];

const errorSelector = createErrorSelector(employerConstants.HYDRATE_DASHBOARD);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    hasFullyOnboarded: hasFullyOnboarded(state),
    companyState: get(state.user.companyState, "state", ""),
  };
};

const mapDispatchToProps = {
  push,
  setTab,
  hydrateDashboard,
  getProgressiveOnboarding,
  getSetupIntent,
  refreshUserState,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  initialFetching: boolean;
  justLeftKyb: boolean;
}

/*
 * Dashboard container is merely a wrapper for whatever subdashboard we are rendering
 * Its only purpose is to determine what subdashboard to render based on a defined rule set
 * This will allow us to concentrate each dashboards rendering/display logic in
 * */

class DashboardContainer extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
      justLeftKyb: false,
    };
  }

  componentDidUpdate(prevProps: Props) {
    const leftKyb =
      includes(stillKybStates, prevProps.companyState) &&
      !includes(stillKybStates, this.props.companyState);
    // eslint-disable-next-line react/no-did-update-set-state -- the poller below only starts on this transition
    if (leftKyb) this.setState({ justLeftKyb: true });
  }

  componentDidMount() {
    window.analytics.page("Employer Dashboard");

    const promises = [
      this.props.getProgressiveOnboarding(
        this.props.client as ApolloClient<object>
      ),
      this.props.getSetupIntent(this.props.client as ApolloClient<object>),
    ];

    void Promise.all(promises).then(() => {
      this.setState({
        initialFetching: false,
      });
    });
  }

  _getActiveDashboard = () => {
    const fullyOnboarded = this.props.hasFullyOnboarded;

    // a failed setup intent (e.g. a mistyped card in the billing portal) must never hold an approved employer here
    if (includes(stillKybStates, this.props.companyState)) {
      return <KybDashboard />;
    } else if (!fullyOnboarded) {
      return <ProgressiveOnboardingDashboard />;
    } else {
      return <EmployerDashboard />;
    }
  };

  render() {
    const activeDashboard = this.state.initialFetching
      ? null
      : this._getActiveDashboard();
    return (
      <div className="dashboard-content">
        {this.state.initialFetching && <IconSpinner centered />}
        {this.props.error && <Alert type="error" msg={this.props.error} />}
        {/* KYB approval comes before the billing hold, so keep checking for a while in case the dashboard gets held */}
        {this.state.justLeftKyb &&
          !includes(stillKybStates, this.props.companyState) && (
            <PromisePoller
              interval={5000}
              promiseToPoll={() =>
                this.props.refreshUserState(
                  this.props.client as ApolloClient<object>
                )
              }
              minutesToPollFor={3}
            />
          )}
        {!this.state.initialFetching && (
          <div className="mega-container" id="employer-dashboard">
            {activeDashboard}
          </div>
        )}
      </div>
    );
  }
}

export default connector(withApollo<Props>(DashboardContainer));
